import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import * as Sentry from "@sentry/react";
import { useSearchParams } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import LoginUsers from "pages/Settings/LoginUsersNew";
import Employees from "pages/EmployeeManagement/Index";
import StateEmployees from "pages/EmployeeManagement/DB-Index";
import iconApplicationAdmin from "assets/images/icons/role-application-admin.png";
import iconEvaluator from "assets/images/icons/role-evaluator.png";
import iconHr from "assets/images/icons/role-hr.png";
import iconItAdmin from "assets/images/icons/role-it-admin.png";
import iconReviewer from "assets/images/icons/role-reviewer.png";
import { tableResize } from "helpers/tableResize";
import TextTruncate from "components/ui/TextTruncate";
import { ReactComponent as IconEmployeeEdit } from "assets/images/icons/employee-edit.svg";
import { ReactComponent as IconUserEdit } from "assets/images/icons/user-edit.svg";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { useDispatch, useSelector } from "react-redux";
import { getModule } from "store/userSlice";
import { getFixedFields } from "store/fieldsSlice";
import { isLobModule, isDbModule } from "helpers/module";
import ListPagination from "components/ui/ListPagination";
import { Button, Input, InputAffix } from "components/ui";
import { deeplinkSearchValue, STAFF_ID_PARAM, USER_ID_PARAM } from "helpers/employeeDeeplink";

const roleIcons = {
  'admin': iconItAdmin,
  'evaluator': iconEvaluator,
  'reviewer': iconReviewer,
};

function AllTab({ tabsComponent, setCounters, setSearchParams, isDbModuleActive }) {

  const { translate, editableTranslate } = useTranslation();

  const fixedFields = useSelector(getFixedFields);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchValue, setSearchValue] = useState('');

  const [users, setUsers] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: 0,
    totalPages: 1,
    perPage: 20
  });

  const [quickFilters, setQuickFilters] = useState(['login_permissions', 'participation_in_performance_based_salary']);

  const [showFilter, setShowFilter] = useState(false);

  const [sortCols, setSortCols] = useState([['first_name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);
  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const filterContainerRef = useRef(null);

  const tabsRef = useRef(null);
  const floatingSeparatorRef = useRef(null);

  const theadRef = useRef(null);

  const tableRef = useRef(null);

  const fixedFieldsObj = useMemo(() => {
    if (!fixedFields) return {};

    const obj = {};
    for (const f of fixedFields) {
      obj[f.slug] = f;
    }
    return obj;
  }, [fixedFields]);

  const objSortCols = useMemo(() => {
    const obj = {};

    for (const sc of sortCols) {
      obj[sc[0]] = sc[1];
    }

    return obj;
  }, [sortCols]);

  useEffect(() => {
    const theadEl = theadRef.current;
    if (!theadEl) return;

    const tabsEl = tabsRef.current;
    if (!tabsEl) return;

    const floatingEl = floatingSeparatorRef.current;
    if (!floatingEl) return;

    const parentEl = theadEl.parentElement;
    const tabsParentEl = tabsEl.parentElement;

    const handleScrollTable = () => {
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const tabsParentCoordinates = tabsParentEl.getBoundingClientRect();
      if (tabsParentCoordinates.y < navHeight) {
        tabsEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7 - 21}px, 0)`;
        theadEl.style.transform = `translate3d(0, ${-coordinates.y + navHeight + tabsEl.offsetHeight + 28}px, 0)`;

        floatingEl.style.display = 'block';
        floatingEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight - 21}px, 0)`;
        floatingEl.style.height = (tabsEl.offsetHeight + theadEl.offsetHeight + 42) + 'px';
      } else {
        tabsEl.style.transform = 'translate3d(0,0,0)';
        theadEl.style.transform = 'translate3d(0,0,0)';

        floatingEl.style.display = 'hidden';
        floatingEl.style.transform = 'translate3d(0,0,0)';
        floatingEl.style.height = '.5rem';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, []);

  const fetchUsers = async (showLoading = true, page = null) => {
    if (!searchValue && quickFilters.length < 1) {
      if (users.length > 0) setCounters(prev => ({ ...prev, all: 0 }));
      setUsers([]);
      setPagination({
        page: 1,
        totalRows: 0,
        totalPages: 1,
        perPage: 20
      });
      return;
    }

    if (!page) page = pagination.page;

    setError(null);
    if (showLoading) setLoading(true);
    try {
      let url = `/users?include=employeeInfo&limit=${pagination.perPage}&page=${page}`;
      if (searchValue) url += `&q=${encodeURIComponent(searchValue)}`;
      else url += `&quick_filters=${quickFilters.join(',')}`;

      if (sortCols && sortCols.length > 0) {
        url += `&sort=` + sortCols.map(sc => sc[0] + ':' + sc[1]).join(',');
      }

      const { data, status } = await api.get(url);
      if (status === 200) {
        setUsers(data.data);
        const temp = data.meta.pagination;
        setPagination({ page, totalRows: +temp.total, totalPages: temp.total_pages, perPage: +temp.per_page });
        setCounters(prev => ({ ...prev, all: +temp.total }));
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch groups'));
      setCounters(prev => ({ ...prev, all: 0 }));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.perPage, quickFilters, sortCols]);

  const minColWidth = {
    roles: 150,
    action: 100
  };

  const maxColWidth = {
    last_name: 300,
    first_name: 300,
    employee_number: 250,
    roles: 150
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'all', false, minColWidth, maxColWidth, setResizedCount);
  }, [users, triggerColWidth, showFilter]);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [showFilter]);

  const handleFilterBlur = (e) => {
    if (filterContainerRef.current && !filterContainerRef.current.contains(e.relatedTarget)) {
      setShowFilter(false);
    }
  };

  const handleCheckQuickFilters = (key, checked) => {
    if (checked) {
      setQuickFilters([...quickFilters, key]);
    } else {
      setQuickFilters(quickFilters.filter(r => r !== key));
    }
  };

  const handleSearchFilter = (e) => {
    e.preventDefault();
    fetchUsers(true, 1);
  };

  const handleFilter = (e) => {
    e?.currentTarget?.blur();
    setShowFilter(!showFilter);
  };

  const handleResetFilter = () => {
    setQuickFilters(['login_permissions', 'participation_in_performance_based_salary']);
    setShowFilter(false);
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field);
      setSortCols(sortCols.filter(sc => sc[0] === field));
    }
  };

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  const handleEditIcon = (tab, user) => {
    const staffId = user?.employeeInfo?.employee_number;
    setSearchParams({
      ...(tab === 'employees' ? {} : { tab }),
      ...(staffId ? { [STAFF_ID_PARAM]: staffId } : { [USER_ID_PARAM]: user?.hash_id })
    });
  };

  return <div>
    <div className="row flex-nowrap">
      <div className={`col-lg ${showFilter ? 'col-md-9 section-col-right' : ''} min-height-after-navbar`}>
        <div className="bg-white w-100 p-4 content-card h-100">
          <div className="position-relative">
            <div ref={floatingSeparatorRef} className="floating-separator"></div>
          </div>
          <div ref={tabsRef} className="d-flex justify-content-between align-items-center border-bottom employee-management-tabs-div mb-4">
            {tabsComponent}
            <div className="position-relative">
              <div className="d-flex align-items-center">
                <form className="form-inline" onSubmit={handleSearchFilter}>
                  <InputAffix className={searchValue ? 'bg-red-dark-soft' : ''} prefix={<i className="bi bi-search mr-2"></i>}>
                    <Input type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} autoComplete="false" placeholder={translate('form.search')} maxLength={255} data-testid="search-input" />
                  </InputAffix>
                </form>
                <div className="position-relative ml-3">
                  <Button variant="secondary" onClick={handleFilter} data-testid="filter-button" className="shadow-none"><i className="bi bi-filter"></i> {editableTranslate('form.filter')}</Button>
                </div>
              </div>
              <div className="employee-filters-separator"></div>
            </div>
          </div>

          {
            error && <div>
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          }

          <div className="row mt-4">
            <div className={'col'}>
              <div className="table-responsive has-custom-scrollbar">
                <table ref={tableRef} className="table common-table login-users-table" data-testid="users-table">
                  <thead ref={theadRef} className="sticky-top">
                    <tr>
                      <th scope="col" className="cursor-pointer">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['first_name'] ? (objSortCols['first_name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('first_name')} data-testid="sort-first-name" />
                          {
                            objSortCols['first_name'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'first_name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('first_name')} data-testid="sort-pin-first-name" />
                          }
                        </span>
                        <span className="th-span" onClick={() => handleChangeSortCol('first_name')} data-testid="header-first-name">
                          {fixedFieldsObj?.first_name?.custom_name || editableTranslate('field.first_name')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        <div className="th-resize-div" data-field={'first_name'}></div>
                      </th>
                      <th scope="col" className="cursor-pointer">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['last_name'] ? (objSortCols['last_name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('last_name')} data-testid="sort-last-name" />
                          {
                            objSortCols['last_name'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'last_name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('last_name')} data-testid="sort-pin-last-name" />
                          }
                        </span>
                        <span className="th-span" onClick={() => handleChangeSortCol('last_name')} data-testid="header-last-name">
                          {fixedFieldsObj?.last_name?.custom_name || editableTranslate('field.last_name')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        <div className="th-resize-div" data-field={'last_name'}></div>
                      </th>
                      <th scope="col" className="cursor-pointer">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['employee_number'] ? (objSortCols['employee_number'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('employee_number')} data-testid="sort-employee-number" />
                          {
                            objSortCols['employee_number'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'employee_number' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('employee_number')} data-testid="sort-pin-employee-number" />
                          }
                        </span>
                        <span className="th-span" onClick={() => handleChangeSortCol('employee_number')} data-testid="header-employee-number">
                          {fixedFieldsObj?.employee_number?.custom_name || editableTranslate('field.employee_number')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        <div className="th-resize-div" data-field={'employee_number'}></div>
                      </th>
                      <th scope="col" className="cursor-pointer">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['roles'] ? (objSortCols['roles'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('roles')} data-testid="sort-roles" />
                          {
                            objSortCols['roles'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'roles' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('roles')} data-testid="sort-pin-roles" />
                          }
                        </span>
                        <span className="th-span" onClick={() => handleChangeSortCol('roles')} data-testid="header-roles">
                          {editableTranslate('global.menu.settings.roles')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        <div className="th-resize-div" data-field={'roles'}></div>
                      </th>
                      <th scope="col">
                        <div className="th-resize-div" data-field={'action'}></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      !loading && users.length > 0 && users.map((user, index) => <tr key={index} className={`tr-hover table-row no-select`}>
                        <td><TextTruncate text={user.first_name} resizedCount={resizedCount} /></td>
                        <td><TextTruncate text={user.last_name} resizedCount={resizedCount} /></td>
                        <td><TextTruncate text={user?.employeeInfo?.employee_number} resizedCount={resizedCount} /></td>
                        <td className="text-nowrap">
                          {
                            (user.roles.includes('it-admin') ? (
                              <span className={`role-icon role-icon-it-admin mr-1`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.it-admin')}>
                                <img src={roleIcons['admin']} />
                              </span>
                            ) : user.roles.includes('application-admin') ? (
                              <span className={`role-icon role-icon-application-admin mr-1`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.application-admin')}>
                                <img src={roleIcons['admin']} />
                              </span>
                            ) : user.roles.includes('hr') ? <span className={`role-icon role-icon-hr mr-1`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.hr')}>
                              <img src={iconHr} />
                            </span>
                              : <span className={`role-icon role-icon-admin mr-1 invisible`}>
                                <img src={roleIcons['admin']} />
                              </span>
                            )
                          }
                          <span className="vr"></span>
                          <span className={`role-icon role-icon-evaluator mr-1 ${(user.roles.filter(role => role.includes('evaluator')).length > 0 && user.groups_evaluating.length > 0) ? '' : 'invisible'}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate(!isDbModuleActive ? 'roles.evaluator' : 'state_employee.first_evaluator')}>
                            <img src={roleIcons['evaluator']} />
                          </span>
                          {
                            isDbModuleActive ? <span className={`role-icon role-icon-secondary-evaluator mr-1 ${user.groups_secondary_evaluating.length > 0 ? '' : 'invisible'}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('state_employee.second_evaluator')}>
                              <img src={roleIcons['evaluator']} />
                            </span> : <span className={`role-icon role-icon-reviewer mr-1 ${(user.roles.filter(role => role.includes('reviewer')).length > 0 && user.groups_reviewing.length > 0) ? '' : 'invisible'}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.reviewer')}>
                              <img src={roleIcons['reviewer']} />
                            </span>
                          }
                        </td>
                        <td className="text-nowrap">
                          {<IconUserEdit onClick={() => handleEditIcon('login_users', user)} className={`cursor-pointer edit-icon ${user.is_login_enabled ? '' : 'invisible'}`} data-tooltip-id="user-edit-tooltip" />}
                          {(user.roles.length === 0 || user.roles.includes('employee')) && <IconEmployeeEdit onClick={() => handleEditIcon('employees', user)} className="cursor-pointer edit-icon" data-tooltip-id="employee-edit-tooltip" />}
                        </td>
                      </tr>)
                    }
                    {
                      loading && <tr>
                        <td colSpan={5}>
                          <div className="d-flex justify-content-center" data-testid="loading">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">Wird geladen...</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    }
                    {
                      !loading && Array.isArray(users) && users.length === 0 && <tr>
                        <td colSpan={5} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                      </tr>
                    }
                  </tbody>
                </table>
                <ReactTooltip id="role-tooltip" place="bottom" />
                <ReactTooltip id="employee-edit-tooltip" place="bottom" content={translate('tooltips.login_users.employee_edit_tooltip')} />
                <ReactTooltip id="user-edit-tooltip" place="bottom" content={translate('tooltips.login_users.user_edit_tooltip')} />
              </div>
              <TableScrollbar tableRef={tableRef} data={users} trigger={resizedCount} />
              <ListPagination
                page={pagination.page}
                perPage={pagination.perPage}
                totalPages={pagination.totalPages}
                totalRows={pagination.totalRows}
                setPage={(page) => setPagination({ ...pagination, page })}
                setPerPage={(perPage) => setPagination({ ...pagination, page: 1, perPage })}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </div>

      {
        showFilter && <div className="col-lg-auto col-md-3 section-col-left min-height-after-navbar">
          <div className="bg-white w-100 p-4 content-card h-100">
            <div className="side-header">
              <h5 className="m-0 font-weight-bold">{editableTranslate('form.filter')}</h5>
              <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={() => setShowFilter(false)} data-testid="close-button">
                <i className="bi bi-x-lg"></i>
              </span>
            </div>

            <div>
              <label className="font-weight-bold">{editableTranslate('form.quick_filters')}</label>

              <div className="form-check mb-2 mt-0">
                <label className="form-check-label">
                  <input className={`form-check-input`} type="checkbox" data-testid="quick-filter-login-permissions" checked={quickFilters.includes('login_permissions')} onChange={e => handleCheckQuickFilters('login_permissions', e.target.checked)} />
                  <span className="ml-1">{editableTranslate('login_users.login_permissions')}</span>
                </label>
              </div>
              <div className="form-check mb-2 mt-0">
                <label className="form-check-label">
                  <input className={`form-check-input`} type="checkbox" data-testid="quick-filter-participation-in-performance-based-salary" checked={quickFilters.includes('participation_in_performance_based_salary')} onChange={e => handleCheckQuickFilters('participation_in_performance_based_salary', e.target.checked)} />
                  <span className="ml-1">{editableTranslate(isDbModuleActive ? 'state_employee.participation_in_evaluation' : 'login_users.participation_in_performance_based_salary')}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>;
}

function Tabs({ activeTab, setSearchParams, counters, module }) {

  const { editableTranslate } = useTranslation();

  const handleChangeTab = (target) => {
    setSearchParams(target ? { tab: target } : {});
  };

  return <div className="employee-management-tabs">
    {
      isLobModule(module) && <div className={`cursor-pointer ${activeTab === 'employees' ? 'active' : ''}`} onClick={() => handleChangeTab('')} data-testid="employees-tab">
        <span>{editableTranslate('global.employees')}</span>
        <span className="counter-badge">{counters.employees}</span>
      </div>
    }
    {
      isDbModule(module) && <div className={`cursor-pointer ${activeTab === 'state_employees' ? 'active' : ''}`} onClick={() => handleChangeTab('')} data-testid="state-employees-tab">
        <span>{editableTranslate('global.tab.state_employees')}</span>
        <span className="counter-badge">{counters.state_employees}</span>
      </div>
    }
    <div className={`cursor-pointer ${activeTab === 'login_users' ? 'active' : ''}`} onClick={() => handleChangeTab('login_users')} data-testid="login-users-tab">
      <span>{editableTranslate('global.menu.settings.login_users', 'Benutzer')}</span>
      <span className="counter-badge">{counters.login_users}</span>
    </div>
    <div className={`cursor-pointer ${activeTab === 'all' ? 'active' : ''}`} onClick={() => handleChangeTab('all')} data-testid="all-tab">
      <span>{editableTranslate('form.all')}</span>
      <span className="counter-badge">{counters.all}</span>
    </div>
  </div>;
}

export default function New() {
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const [activeTab, setActiveTab] = useState(null);

  const [search, setSearch] = useState(deeplinkSearchValue(searchParams));

  const [counters, setCounters] = useState({
    employees: 0,
    state_employees: 0,
    login_users: 0,
    all: 0
  });

  useEffect(() => {
    let tab = searchParams.get('tab') || '';
    setActiveTab(tab);
    setSearch(deeplinkSearchValue(searchParams));
  }, [searchParams, isDbModuleActive]);

  useEffect(() => {
    fetchCounters();
  }, [activeTab]);

  const fetchCounters = async () => {
    try {
      const { data, status } = await api.get(`/employees/counters`);
      if (status === 200) {
        setCounters(data.data);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  return <div>
    {
      (isLobModule(currentModule) && !['login_users', 'all'].includes(activeTab)) && <Employees tabsComponent={<Tabs activeTab={'employees'} setSearchParams={setSearchParams} counters={counters} module={currentModule} />} search={search} setCounters={setCounters} activeTab={'employees'} />
    }
    {
      (isDbModuleActive && !['login_users', 'all'].includes(activeTab)) && <StateEmployees tabsComponent={<Tabs activeTab={'state_employees'} setSearchParams={setSearchParams} counters={counters} module={currentModule} />} search={search} setCounters={setCounters} activeTab={'state_employees'} />
    }
    {
      activeTab === 'login_users' && <LoginUsers tabsComponent={<Tabs activeTab={activeTab} setSearchParams={setSearchParams} counters={counters} module={currentModule} />} search={search} setCounters={setCounters} />
    }
    {
      activeTab === 'all' && <AllTab tabsComponent={<Tabs activeTab={activeTab} setSearchParams={setSearchParams} counters={counters} module={currentModule} />} setCounters={setCounters} setSearchParams={setSearchParams} isDbModuleActive={isDbModuleActive} />
    }
  </div>;
}
